/**
 * RedeploymentCenterV2.utils.js
 *
 * Stateless helpers behind the Redeployment Center page — no hooks, no
 * fetching — so the column plumbing and CSV shaping can be read (and
 * unit-tested) apart from the component tree.
 */

import { ConfigProvider } from "antd";
import { SbTag, SbTooltip } from "uiCore";
import { getScoreColor } from "Utils/scoreColor.utils";
import { IndexInfoLabel } from "../components/IndexTooltip";
import ShortlistReasonCell from "../components/ShortlistReasonCell";

/** Distinct values from a precomputed list, as AntD column filter options —
 * the primitive behind getFilters, for columns whose cells render derived
 * values (full names, nested fields) rather than a flat row key. */
export const toFilterOptions = (values) =>
  [...new Set(values.filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map((value) => ({ text: value, value }));

/** Distinct values of `key` across rows, as AntD column filter options. */
export const getFilters = (rows, key) => toFilterOptions(rows.map((row) => row[key]));

/**
 * The "Employee ID + person name" filter-column pair every ranked-people
 * table opens with (Reduction Detail, both View Planning drill-downs, Role
 * Detail's candidates). Rows carry a nested `person` — the id shows the HRIS
 * employeeId (nullable → "—"), never the opaque person/user id. The name
 * column's cell can be overridden (`renderName`, e.g. to append a shortlist
 * tag) — its filter still matches on person.name either way.
 */
export const buildPersonColumns = (rows, { nameTitle = "Person", renderName } = {}) => {
  const persons = rows.map((row) => row.person || {});
  return [
    {
      title: "Employee ID",
      dataIndex: ["person", "employeeId"],
      filters: getFilters(persons, "employeeId"),
      filterSearch: true,
      onFilter: (value, record) => record.person?.employeeId === value,
      render: (v) => v || "—",
    },
    {
      title: nameTitle,
      key: "name",
      filters: getFilters(persons, "name"),
      filterSearch: true,
      onFilter: (value, record) => record.person?.name === value,
      render: renderName || ((_, record) => record.person?.name),
    },
  ];
};

/**
 * The SMI match-score column every ranked-people table renders — info-icon
 * title (full index name on hover), thresholds-colored tag, null-safe
 * sorter. `showSorterTooltip` is off because the info icon and antd's
 * default "Click to sort" header tooltip would overlap. `extra` merges
 * per-table overrides (e.g. defaultSortOrder).
 *
 * A null value (the person hasn't updated their profile against this role
 * yet — "not scored", not "scored zero") renders as a dash + tooltip
 * instead of a percentage, same convention as NextRoleMatchTag and
 * RoleDetailV2's own SMI column for their equivalent case. A caller that
 * needs different wording/styling for that case can still override `render`
 * via `extra` (e.g. PersonNextRolesTable's SMI Match column).
 * `filterLabel` gives SbTable's auto column filter the same "NN%" text the
 * tag shows (null scores fall into its "(Blank)" option).
 * `largeTag: true` renders the score tag at the table's 13px body size
 * instead of antd's small tag size (~11px) — opt-in per table.
 */
// antd sizes Tag text with the small font token; raising it through antd's
// own theme tokens keeps this free of custom CSS.
const LARGE_SCORE_TAG_THEME = { components: { Tag: { fontSizeSM: 13 } } };

export const buildScoreColumn = (title, dataIndex, thresholds, extra = {}) => {
  // `index` picks which acronym the title's info tooltip spells out
  // (INDEX_FULL_NAMES) and `largeTag` the tag size — both ours, not antd's,
  // so keep them off the column.
  const { index = "SMI", largeTag = false, ...columnExtra } = extra;
  const renderTag = (value) => <SbTag color={getScoreColor(value, thresholds)}>{value}%</SbTag>;
  return {
    title: <IndexInfoLabel index={index}>{title}</IndexInfoLabel>,
    dataIndex,
    align: "center",
    showSorterTooltip: false,
    sorter: (a, b) => (a[dataIndex] ?? -1) - (b[dataIndex] ?? -1),
    filterLabel: (value) => `${value}%`,
    render: (value) => {
      if (value == null) return <SbTooltip content="User has not updated profile">—</SbTooltip>;
      return largeTag ? <ConfigProvider theme={LARGE_SCORE_TAG_THEME}>{renderTag(value)}</ConfigProvider> : renderTag(value);
    },
    ...columnExtra,
  };
};

/**
 * The "Reason" column both redeployment reduction tables end with (Reduction
 * Detail's live draft and View Planning's saved-plan drill-down) — an icon
 * showing the employee's Reason for Shortlisting on hover (ShortlistReasonCell),
 * rendered only for employees that have one, whatever their status. A
 * Retained row without one shows the same "—" placeholder the Employee ID
 * column uses; an At Risk row without one stays blank.
 * `reasonByPersonId` maps person id -> reason; `isRetained(record)` is the
 * caller's own status rule, since each table ranks rows its own way.
 * `onEdit(record)`, when passed, adds an Edit action to the reason popover
 * (omit it for a read-only table). The click stop keeps clicks on the icon
 * or popover from toggling the row's expandable panel behind it.
 */
export const buildShortlistReasonColumn = (reasonByPersonId, isRetained, onEdit) => ({
  title: "Reason",
  key: "shortlistReason",
  align: "center",
  width: 90,
  onHeaderCell: () => ({ className: "tm-col-header-center" }),
  render: (_, record) => {
    const reason = reasonByPersonId.get(record.person?.id);
    if (!reason) return isRetained(record) ? "—" : null;
    return (
      <span onClick={(e) => e.stopPropagation()}>
        <ShortlistReasonCell reason={reason} onEdit={onEdit && (() => onEdit(record))} />
      </span>
    );
  },
});

/**
 * Standard top filters shared by both Role View and Employee View — both row
 * shapes expose the same six fields (see useRedeploymentCandidates), so the
 * filter row works identically regardless of which view is active.
 */
export const buildStandardFilterColumns = (rows) => [
  { title: "Legal Entity", dataIndex: "legalEntity", filters: getFilters(rows, "legalEntity"), filterSearch: true, onFilter: (v, r) => r.legalEntity === v },
  { title: "Business Unit", dataIndex: "businessUnit", filters: getFilters(rows, "businessUnit"), filterSearch: true, onFilter: (v, r) => r.businessUnit === v },
  { title: "Function", dataIndex: "function", filters: getFilters(rows, "function"), filterSearch: true, onFilter: (v, r) => r.function === v },
  { title: "Sub-function", dataIndex: "subFunction", filters: getFilters(rows, "subFunction"), filterSearch: true, onFilter: (v, r) => r.subFunction === v },
  { title: "Level", dataIndex: "level", filters: getFilters(rows, "level"), filterSearch: true, onFilter: (v, r) => r.level === v },
  { title: "Location", dataIndex: "location", filters: getFilters(rows, "location"), filterSearch: true, onFilter: (v, r) => r.location === v },
];

/**
 * Whether an Employee View row can expand into its "next roles" panel.
 * Mock rows expand when their precomputed array is non-empty; API rows
 * (nextRoles: null, loaded lazily on expand) expand when the listing's
 * next_roles_count says there is something to load — or when the count is
 * unknown, since only fetching can tell.
 */
export const isPersonRowExpandable = (person) => {
  if (person.nextRoles) return person.nextRoles.length > 0;
  return person.nextRolesCount == null || person.nextRolesCount > 0;
};

/**
 * Flattens the reducing roles into the Reduction Summary CSV rows — one row
 * per (role, candidate), or a single placeholder row for a role with no
 * candidates so the role still appears in the export.
 */
export const buildReductionSummaryCsvRows = (reducingRoles, availableMap) =>
  reducingRoles.flatMap((role) => {
    const targetReduction = `${role.currentPositionCount} → ${role.targetPositionCount}`;
    const atRiskHeadcount = Math.max(role.currentPositionCount - role.targetPositionCount, 0);

    if (!role.candidates.length) {
      return [{
        Role: role.title,
        "Target Reduction": targetReduction,
        "At Risk Headcount": atRiskHeadcount,
        Person: "—",
        "SMI %": "—",
        "Marked Available": "—",
      }];
    }

    return role.candidates.map((candidate) => ({
      Role: role.title,
      "Target Reduction": targetReduction,
      "At Risk Headcount": atRiskHeadcount,
      Person: candidate.person?.name,
      "SMI %": candidate.sri,
      "Marked Available": availableMap[candidate.personId] ? "Yes" : "No",
    }));
  });
